import { useCallback } from 'react';

import { IsoCountryCode3 } from '@rbilabs/intl';
import { useIntl } from 'react-intl';

import { useSaveDeliveryAddressMutation } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { useOrderContext } from 'state/order';
import { PHONE_NUMBER_DUMMY } from 'utils/constants';
import { LocalStorage, StorageKeys } from 'utils/local-storage';
import { toast } from 'utils/toast';

import { IDeliveryAddressLocation } from './types';

interface UseSaveDeliveryAddressParams {
  onClose: VoidFunction;
  onSaveDeliveryAddress: VoidFunction;
  // should this be removed after we set the enable-saved-addresses-improvements flag to TRUE for all the apps?
  checkedLocation?: string;
}

export const useSaveDeliveryAddress = ({
  onClose,
  checkedLocation,
  onSaveDeliveryAddress,
}: UseSaveDeliveryAddressParams) => {
  const { formatMessage } = useIntl();
  const { logEvent } = useCdpContext();
  const { refreshCurrentUser } = useAuthContext();
  const { setSavedDeliveryAddress: setSavedDeliveryAddressOrder } = useOrderContext();

  const [saveDeliveryAddressMutation, { loading, error }] = useSaveDeliveryAddressMutation({
    onCompleted: () => {
      refreshCurrentUser();
    },
    onError: () => {
      refreshCurrentUser();
    },
  });

  const saveDeliveryAddress = useCallback(
    async (deliveryAddress: IDeliveryAddressLocation | undefined) => {
      if (deliveryAddress) {
        // Save the details to local storage too so we can use it on checkout
        LocalStorage.setItem(
          StorageKeys.DELIVERY_ADDRESS,
          JSON.stringify({
            ...deliveryAddress?.address,
            location: checkedLocation ?? deliveryAddress.address.alias,
          })
        );

        const addressToSave = {
          addressLine1: deliveryAddress.address.addressLine1,
          addressLine2: deliveryAddress.address.addressLine2,
          city: deliveryAddress.address.city,
          country:
            IsoCountryCode3[deliveryAddress.address.country as keyof typeof IsoCountryCode3] ?? '',
          latitude: deliveryAddress.coordinates.lat,
          phoneNumber: PHONE_NUMBER_DUMMY,
          longitude: deliveryAddress.coordinates.lng,
          state: deliveryAddress.address.state,
          route: deliveryAddress.address.route,
          streetNumber: deliveryAddress.address.streetNumber,
          zip: deliveryAddress.address.zip,
          alias: checkedLocation ?? deliveryAddress.address.alias,
        };

        saveDeliveryAddressMutation({
          variables: {
            input: addressToSave,
          },
        }).then(({ errors }) => {
          if (!errors) {
            toast.success(formatMessage({ id: 'deliveryAddressSavedSuccessfully' }));
            logEvent(CustomEventNames.SAVE_ADDRESS, EventTypes.UserPreference, addressToSave);

            onSaveDeliveryAddress();
            //Set Saved Address here, because its will used in detail cart
            setSavedDeliveryAddressOrder({
              address: {
                ...deliveryAddress?.address,
                location: checkedLocation ?? deliveryAddress?.address.alias,
              },
              coordinates: deliveryAddress?.coordinates,
            });
          } else {
            toast.error(formatMessage({ id: 'errorToSaveDeliveryAddress' }));
            onSaveDeliveryAddress();
          }

          onClose();
        });
      }
    },
    [
      onClose,
      logEvent,
      formatMessage,
      checkedLocation,
      onSaveDeliveryAddress,
      saveDeliveryAddressMutation,
      setSavedDeliveryAddressOrder,
    ]
  );

  return {
    error,
    loading,
    saveDeliveryAddress,
  };
};
